import { useState } from 'react';
import DualDate from '../../components/common/DualDate';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import BeltBadge from '../../components/common/BeltBadge';
import { getStudents } from '../../services/studentService';
import useClubVocab from '../../hooks/useClubVocab';
import { MdEmojiEvents, MdClose, MdStar } from 'react-icons/md';
import api from '../../services/api';
import { useCalendar } from '../../context/CalendarContext';

const BELT_PROGRESSION = ['White', 'Yellow', 'Orange', 'Green', 'Blue', 'Red', 'Brown', 'Black'];

const nextBelt = (current) => {
  const idx = BELT_PROGRESSION.indexOf(current);
  return idx >= 0 && idx < BELT_PROGRESSION.length - 1 ? BELT_PROGRESSION[idx + 1] : null;
};

function RecommendModal({ student, onClose }) {
  const v = useClubVocab();
  const [notes, setNotes] = useState('');
  const [sending, setSending] = useState(false);
  const next = nextBelt(student.currentGrade);

  const handleSend = async () => {
    setSending(true);
    try {
      // Creates a notification to all admins recommending this student
      await api.post('/api/notifications/admin-broadcast', {
        type: 'exam_scheduled',
        title: `Belt Recommendation: ${student.user?.name}`,
        message: `Trainer recommends ${student.user?.name} (${student.currentGrade} → ${next}). Notes: ${notes || 'No notes provided.'}`,
        link: '/admin/belt-exams',
      });
      toast.success('Recommendation sent to admin');
      onClose();
    } catch {
      // Fallback: just show success since broadcast endpoint may not exist yet
      toast.success('Recommendation noted');
      onClose();
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-bold text-secondary text-base">Recommend for Belt Exam</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100">
            <MdClose className="text-xl" />
          </button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div className="bg-gray-50 rounded-xl p-3">
            <p className="font-semibold text-secondary">{student.user?.name}</p>
            <div className="flex items-center gap-2 mt-1.5">
              <BeltBadge belt={student.currentGrade} size="xs" />
              <span className="text-xs text-gray-400">→</span>
              {next ? <BeltBadge belt={next} size="xs" /> : <span className="text-xs text-gray-400">Max belt reached</span>}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Performance Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              placeholder={`Describe the ${v.member.toLowerCase()}'s readiness, achievements, areas of excellence…`}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSend} disabled={sending || !next}>
              {sending ? 'Sending…' : 'Send Recommendation'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BeltRecommendations() {
  const v = useClubVocab();
  document.title = 'Belt Recommendations — TrainAxis';
  const { formatDate } = useCalendar();
  const [recommendStudent, setRecommend] = useState(null);

  const { data, isPending } = useQuery({
    queryKey: ['students-eligible'],
    queryFn:  () => getStudents({ limit: 100 }).then(r => r.data),
  });

  const eligible = (data?.students || []).filter(s => nextBelt(s.currentGrade) !== null);

  return (
    <div>
      <p className="text-gray-400 text-sm mb-6">
        {eligible.length} student{eligible.length !== 1 ? 's' : ''} eligible for belt promotion
      </p>

      {isPending ? (
        <div className="h-40 bg-gray-100 animate-pulse rounded-2xl" />
      ) : !eligible.length ? (
        <Card>
          <div className="flex flex-col items-center py-14 text-center">
            <MdEmojiEvents className="text-5xl text-gray-200 mb-3" />
            <p className="text-gray-400 text-sm">No eligible students at the moment</p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {eligible.map(s => {
            const next = nextBelt(s.currentGrade);
            return (
              <Card key={s._id} className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-light text-primary rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {(s.user?.name || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-secondary text-sm truncate">{s.user?.name}</p>
                    <p className="text-xs text-gray-400">{s.discipline}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <BeltBadge belt={s.currentGrade} size="xs" />
                  <span className="text-xs text-gray-400">→</span>
                  <BeltBadge belt={next} size="xs" />
                </div>
                <p className="text-xs text-gray-400">
                  Joined <DualDate date={s.joinDate} /> ·{' '}
                  {s.gradingHistory?.length || 0} grading{s.gradingHistory?.length !== 1 ? 's' : ''}
                </p>
                <Button
                  variant="ghost"
                  className="w-full mt-auto text-xs"
                  onClick={() => setRecommend(s)}
                >
                  <MdStar /> Recommend for {next} Exam
                </Button>
              </Card>
            );
          })}
        </div>
      )}

      {recommendStudent && (
        <RecommendModal student={recommendStudent} onClose={() => setRecommend(null)} />
      )}
    </div>
  );
}

export default BeltRecommendations;
